import { FC, useRef } from 'react';

import { ServerError } from 'api/types/error';
import { DialogConfirmRevokeOtpVerification, SendCodeTimer } from 'components';
import { parseFullName } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Button, Chip } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import {
  DialogChangeEmail,
  DialogDeleteAndReasonChange,
  DialogReasonChange,
  ClientFormContent,
} from 'modules/client/common/components';
import {
  ActionProps,
  ActionType,
} from 'modules/client/common/components/ClientFormContent/types';
import {
  LockAndUnlockReadOnlyProps,
  useLockAndUnlockReadOnly,
} from 'modules/client/common/hooks';

import { useFormContext } from '../../../forms/legalClientBeneficiaryForm';
import {
  LegalClientBeneficiarySections,
  LegalClientBeneficiarySectionsProps,
} from '../../LegalClientBeneficiarySections';

export interface FormContentProps
  extends LockAndUnlockReadOnlyProps,
    LegalClientBeneficiarySectionsProps {
  submitLoading?: boolean;
  deleteLoading?: boolean;
  dearchiveLoading?: boolean;
  archiveLoading?: boolean;
  changeEmailLoading?: boolean;
  changeEmailError?: ServerError;
  resendVerificationEmailTime?: number;
  revokeOtpVerificationLoading?: boolean;
  onShowVersionHistory?: () => void;
  onDelete?: (reason: string) => void;
  onSendVerificationEmail?: () => void;
  onChangeEmail?: (email: string, reason: string) => Promise<boolean>;
  onArchive?: (reason: string) => Promise<boolean>;
  onDearchive?: (reason: string) => Promise<boolean>;
  onRevokeOtpVerification?: () => Promise<any>;
}

export const FormContent: FC<FormContentProps> = ({
  submitLoading,
  deleteLoading,
  changeEmailLoading,
  resendVerificationEmailTime,
  changeEmailError,
  dearchiveLoading,
  archiveLoading,
  revokeOtpVerificationLoading,
  unlockClient,
  lockClientIfPossible,
  onShowVersionHistory,
  onDelete,
  onSendVerificationEmail,
  onChangeEmail,
  onArchive,
  onDearchive,
  onRevokeOtpVerification,
  ...sectionProps
}) => {
  const { t } = useTranslation();
  const { resetForm, initialValues, setReadOnly, readOnly } = useFormContext();

  const fullName = initialValues
    ? parseFullName(initialValues.personalData)
    : '';

  const headerTitle = fullName
    ? t('client.legal.legalClientBeneficiary.title', {
        name: fullName,
      })
    : '';

  const { onDisableReadOnly, onEnableReadOnly } = useLockAndUnlockReadOnly(
    setReadOnly,
    { unlockClient, lockClientIfPossible }
  );

  const { open: openDeleteDialog, ...deleteDialogProps } = useDialog();

  const { open: openChangeEmailDialog, ...changeEmailDialogProps } =
    useDialog();

  const { open: openDialogReason, ...dialogReasonProps } = useDialog();

  const {
    isOpen: isOpenRevokeOtp,
    onClose: onCloseRevokeOtp,
    open: openRevokeOtp,
  } = useDialog();

  const actionRef = useRef<ActionType>();

  const isArchived = onDearchive !== undefined;

  const onActionArchive = () => {
    actionRef.current = ActionType.archive;
    openDialogReason();
  };

  const onActionDearchive = () => {
    actionRef.current = ActionType.dearchive;
    openDialogReason();
  };

  const handleChangeEmail = async (email: string, reason: string) => {
    if (await onChangeEmail?.(email, reason)) {
      changeEmailDialogProps.onClose();
    }
  };

  const onSaveReason = async (reason: string) => {
    if (actionRef.current === ActionType.archive) {
      if (await onArchive?.(reason)) {
        dialogReasonProps.onClose();
      }
    } else if (actionRef.current === ActionType.dearchive) {
      if (await onDearchive?.(reason)) {
        dialogReasonProps.onClose();
      }
    }
  };

  const onActionRevokeOtpVerification = () => {
    openRevokeOtp();
  };

  const onConfirmRevokeOtp = async () => {
    await onRevokeOtpVerification?.().finally(() => onCloseRevokeOtp());
  };

  const actionMenuProps: ActionProps = {
    showEditAction: !onDearchive,
    onDelete: onDelete ? openDeleteDialog : undefined,
    onArchive: onArchive ? onActionArchive : undefined,
    onDearchive: onDearchive ? onActionDearchive : undefined,
    onRevokeOtpVerification: onRevokeOtpVerification
      ? onActionRevokeOtpVerification
      : undefined,
  };

  return (
    <>
      <ClientFormContent
        actionMenuProps={actionMenuProps}
        content={<LegalClientBeneficiarySections {...sectionProps} />}
        headerAfterContent={
          <Chip
            size="s"
            text={
              isArchived ? t('common.archivedStatus') : t('common.activeStatus')
            }
            variant={isArchived ? 'gray' : 'green'}
          />
        }
        headerRightContent={
          <>
            {onChangeEmail && readOnly && !onDearchive && (
              <Button
                colorVariant="gray"
                label={t('client.changeEmailLabel')}
                mode="outlined"
                sizeVariant="s"
                onClick={openChangeEmailDialog}
              />
            )}
            {onSendVerificationEmail && readOnly && !onDearchive && (
              <SendCodeTimer
                buttonLabel={t('common.resendEmail')}
                seconds={resendVerificationEmailTime}
                onClickSend={onSendVerificationEmail}
              />
            )}
          </>
        }
        headerTitle={headerTitle}
        resetForm={resetForm}
        shownTabBar={false}
        submitLoading={submitLoading}
        onEdit={onDisableReadOnly}
        onEnableReadOnly={onEnableReadOnly}
        onShowVersionHistory={onShowVersionHistory}
      />
      {onDelete && (
        <DialogDeleteAndReasonChange
          {...deleteDialogProps}
          deleteLoading={deleteLoading}
          onDelete={onDelete}
        />
      )}

      {onChangeEmail && (
        <DialogChangeEmail
          {...changeEmailDialogProps}
          changeEmailError={changeEmailError}
          submitLoading={changeEmailLoading}
          onSubmit={(v) => handleChangeEmail(v.email, v.reason)}
        />
      )}

      <DialogReasonChange
        submitLoading={archiveLoading ?? dearchiveLoading}
        onSave={onSaveReason}
        {...dialogReasonProps}
      />

      <DialogConfirmRevokeOtpVerification
        fullName={fullName}
        isOpen={isOpenRevokeOtp}
        loading={revokeOtpVerificationLoading}
        onClose={onCloseRevokeOtp}
        onConfirm={onConfirmRevokeOtp}
      />
    </>
  );
};
